'use client';

import { useLogger } from 'next-axiom';
import { usePathname } from 'next/navigation';
import { useLayoutEffect, useRef } from 'react';
import { invalidateElevarContext, useElevarEvents } from './index';

// Global tracking to prevent multiple instances and component remounting issues
const globalTrackingState = {
  lastTrackedPath: '',
  isTracking: false,
  trackingPromise: null as Promise<void> | null,
  firedPaths: new Set<string>(), // Track paths that have fired during this browser session
  currentSessionFired: new Map<string, number>(), // Track path + timestamp for rapid-fire protection
};

export function ElevarRouteTracker() {
  const pathname = usePathname();
  const { trackUserData } = useElevarEvents();
  const logger = useLogger();

  // Track the last processed pathname for this component instance
  const lastTrackedPathRef = useRef<string | null>(null);
  // Track if we're currently processing to prevent concurrent executions
  const isProcessingRef = useRef(false);

  // Handle both initial mount and route changes in a single effect
  // biome-ignore lint/correctness/useExhaustiveDependencies: Intentionally exclude trackUserData and logger to prevent double firing on data loading
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
  useLayoutEffect(() => {
    // Skip if pathname hasn't actually changed for this instance
    if (pathname === lastTrackedPathRef.current) {
      return;
    }

    // Skip if we're already processing this pathname
    if (isProcessingRef.current) {
      return;
    }

    // Clear fired paths when navigating to a different route to allow re-tracking on return
    if (
      globalTrackingState.lastTrackedPath &&
      globalTrackingState.lastTrackedPath !== pathname
    ) {
      // Don't clear the current path we're navigating away from, but clear others
      for (const path of globalTrackingState.firedPaths) {
        if (path !== globalTrackingState.lastTrackedPath && path !== pathname) {
          globalTrackingState.firedPaths.delete(path);
        }
      }
    }

    // Component remounting protection: skip if this path has already fired in this browser session
    if (globalTrackingState.firedPaths.has(pathname)) {
      lastTrackedPathRef.current = pathname;
      return;
    }

    // Race condition protection: skip if this path has fired very recently (within 100ms)
    const now = Date.now();
    const lastFired = globalTrackingState.currentSessionFired.get(pathname);
    if (lastFired && now - lastFired < 100) {
      lastTrackedPathRef.current = pathname;
      return;
    }

    // Global deduplication: skip if this pathname is already being tracked globally
    if (
      globalTrackingState.isTracking &&
      globalTrackingState.lastTrackedPath === pathname
    ) {
      // Wait for the global tracking to complete, then update our local state
      globalTrackingState.trackingPromise?.finally(() => {
        lastTrackedPathRef.current = pathname;
      });
      return;
    }

    // Mark as processing to prevent concurrent executions
    isProcessingRef.current = true;
    globalTrackingState.isTracking = true;
    globalTrackingState.lastTrackedPath = pathname;
    globalTrackingState.firedPaths.add(pathname); // Mark this path as fired in this session
    globalTrackingState.currentSessionFired.set(pathname, now); // Mark this path as fired with timestamp

    // Update the tracked pathname before processing to prevent race conditions
    lastTrackedPathRef.current = pathname;

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    const executeTracking = () => {
      // Get tracking functions inside the effect to avoid dependency issues
      const currentTrackUserData = trackUserData;
      const currentLogger = logger;

      try {
        // Fire dl_user_data as early as possible with minimal delay
        invalidateElevarContext();
        currentTrackUserData();
      } catch (error) {
        currentLogger.error('Elevar route tracking error', {
          error: error instanceof Error ? error.message : String(error),
          pathname,
          component: 'ElevarRouteTracker',
          url: typeof window !== 'undefined' ? window.location.href : undefined,
        });
      } finally {
        // Reset processing flags
        isProcessingRef.current = false;
        globalTrackingState.isTracking = false;
        globalTrackingState.trackingPromise = null;

        // Cleanup old entries from the session map (older than 5 seconds)
        const fiveSecondsAgo = Date.now() - 5000;
        for (const [
          path,
          timestamp,
        ] of globalTrackingState.currentSessionFired.entries()) {
          if (timestamp < fiveSecondsAgo) {
            globalTrackingState.currentSessionFired.delete(path);
          }
        }
      }
    };

    // Execute tracking immediately and mark as complete
    executeTracking();
    globalTrackingState.trackingPromise = Promise.resolve();
  }, [pathname]);

  return null;
}
